import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Checkbox, Dialog, DialogContent, Divider, FormControlLabel, IconButton } from "@mui/material";
import styled from "@emotion/styled";
import { useRecoilValue } from "recoil";
import parse from "html-react-parser";
import { HiX } from "react-icons/hi";
import semver from "semver";

import { appParamState } from "../../atoms";
import {
  CLIENT,
  FLOW,
  GLOBAL_CONST,
  PLATFORM,
  REWARD_OFFERING,
} from "../../constants";
import {
  getPlatformFormattedText,
  redirect,
  redirectLoaded,
  replaceGlobalConfigVars,
  sendAnalytics,
  wireCallbacks,
} from "../../utils";
import OnboardingStep from "./OnboardingStep";
import OnboardingContent from "./OnboardingContent";
import StickyCTA from "./OnboardingStickyCTA";
import Dropdown from "./Dropdown";
import DropdownContent from "./DropdownContent";
import CouponBox from "./CouponBox";
import RewardsButton from "./RewardsButton";

const HelpHeading = styled.div`
  text-align: left;
  width: 100%;
`;

const OptionsContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionLabel = styled(FormControlLabel)`
  margin: 0;
  align-items: flex-start;
  gap: 12px;
  .MuiFormControlLabel-label {
    font-weight: 400;
    font-size: 0.875rem;
    color: #2f383c;
    line-height: 1.35;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 4px;
  margin-top: 2px;
`;

const UncheckedIcon = styled("span")`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #14a248;
  display: inline-block;
`;

const CheckedIcon = styled(UncheckedIcon)`
  background-color: #14a248;
  border-color: #14a248;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #ffffff;
  }
`;

const SelectionDialog = styled(Dialog)`
  .MuiDialog-container {
    align-items: flex-end;
  }

  .MuiPaper-root {
    width: 100%;
    margin: 0;
    border-radius: 16px 16px 0 0;
    background-color: #ffffff;
    padding: 16px 15px 45px;
  }

  .MuiBackdrop-root {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const DialogHeader = styled.div`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
`;

const DialogTitle = styled.h2`
  margin: 0;
  font-family: "Open Sans", sans-serif;
  font-size: var(--Font-Base-Body-1-Size, 16px);
  font-weight: 600;
  line-height: var(--Font-Base-Body-1-Line-Height, 22px);
  letter-spacing: -0.32px;
  color: var(--UI-Text-Dark---High-Priority, rgba(0, 0, 0, 0.9));
  width: 100%;
  text-align: center;
`;

const DialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: var(--Colour-Semantic-Font-Secondary, #515151);
  font-family: "Open Sans", sans-serif;
  font-size: var(--Font-Base-Body-1-Size, 16px);
  font-weight: 400;
  line-height: var(--Font-Base-Body-1-Line-Height, 22px);
  letter-spacing: -0.32px;
  text-align: center;
  align-items: center;
`;

const DialogFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 45px;
`;

const CloseIconButton = styled(IconButton)`
  padding: 4px;
  color: #2f383c;
  position: absolute;
  top: 0;
  right: 0;
`;

type AdobeCompetitionOnboardingProps = {
  redeemUrl: string;
  rewardId: REWARD_OFFERING;
  partnerCfg?: { onboarding1: Onboarding; coupon: Coupon; vars: ConfigVars };
  coupon?: string | null;
  viewType?: "external" | "tab";
};

function AdobeCompetitionOnboarding({
  redeemUrl,
  rewardId,
  partnerCfg,
  coupon,
  viewType = "tab",
}: AdobeCompetitionOnboardingProps) {
  const { flow } = useRecoilValue(appParamState);
  const redeemUrlEncoded = encodeURIComponent(redeemUrl);
  const globalPartnerCfg = GLOBAL_CFG[rewardId];

  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showSelectionPrompt, setShowSelectionPrompt] = useState(false);
  const [stickyHeight, setStickyHeight] = useState(0);
  const basePadding = GLOBAL_CONST.PLATFORM === PLATFORM.ANDROID ? 320 : 200;
  const contentPaddingBottom = Math.max(basePadding, stickyHeight + 32);

  const handleStickyHeightChange = useCallback((height: number) => {
    setStickyHeight(height);
  }, []);

  const checkboxOptions = useMemo(
    () => globalPartnerCfg.onboarding1.checkboxOptions ?? [],
    [globalPartnerCfg.onboarding1.checkboxOptions]
  );
  const dropdownHeading = globalPartnerCfg.onboarding1.dropdownHeading;
  const dropdowns = globalPartnerCfg.onboarding1.dropdowns ?? [];

  const selectionPrompt = globalPartnerCfg.onboarding1.selectionPrompt ?? {
    title: "Review & Accept",
    description: "Please accept the T&Cs and confirm if you wish to enter the competition.",
    ctaText: "Ok, got it",
  };

  useEffect(() => {
    wireCallbacks();
  }, []);

  useEffect(() => {
    if (flow === FLOW.ONBOARDING || flow === FLOW.REDEEM) {
      redirectLoaded();
      void sendAnalytics("onboarding1ScreenOnLoad");
      void redirect(`${GLOBAL_CONST.CLIENT_ID}://rewards/onboarding-complete`);
    }
  }, [flow]);

  const isLegacy = useCallback(() => {
    const isLinktLegacy = semver.lte(GLOBAL_CONST.APP_VERSION, "4.0.0");
    const isLinktgoLegacy = semver.lte(GLOBAL_CONST.APP_VERSION, "3.0.1");
    return (
      (GLOBAL_CONST.CLIENT_ID === CLIENT.LINKT && isLinktLegacy) ||
      (GLOBAL_CONST.CLIENT_ID === CLIENT.LINKTGO && isLinktgoLegacy)
    );
  }, []);

  const handleOptionChange = useCallback(
    (option: SelectionOption) => (event: ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const deselectKey = option.deselectAnalyticsKey ?? `${option.analyticsKey}-deselected`;

      if (checked) {
        if (selectedOption && selectedOption !== option.analyticsKey) {
          const previousOption = checkboxOptions.find((opt) => opt.analyticsKey === selectedOption);
          const previousDeselectKey = previousOption?.deselectAnalyticsKey ?? `${selectedOption}-deselected`;
          void sendAnalytics(previousDeselectKey);
        }

        setSelectedOption(option.analyticsKey);
        void sendAnalytics(option.analyticsKey);
        if (showSelectionPrompt) {
          setShowSelectionPrompt(false);
        }
        return;
      }

      if (selectedOption === option.analyticsKey) {
        void sendAnalytics(deselectKey);
        setSelectedOption(null);
      }
    },
    [checkboxOptions, selectedOption, showSelectionPrompt]
  );

  const handleCtaClick = useCallback(async () => {
    if (checkboxOptions.length > 0 && !selectedOption) {
      setShowSelectionPrompt(true);
      return;
    }

    await sendAnalytics("onboarding1AcceptButton");
    const baseUrl = `${GLOBAL_CONST.CLIENT_ID}://rewards/link?url=${redeemUrlEncoded}`;
    await redirect(isLegacy() ? baseUrl : `${baseUrl}&view-type=${viewType}`);
  }, [checkboxOptions.length, isLegacy, redeemUrlEncoded, selectedOption, viewType]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <Box
        sx={{
          flex: 1,
          padding: "24px",
          paddingBottom: `${contentPaddingBottom}px`,
        }}
      >
        <OnboardingStep
          image={globalPartnerCfg.onboarding1.image}
          imageLottie={globalPartnerCfg.onboarding1.lottie}
        >
          <OnboardingContent iconUrl={globalPartnerCfg.onboarding1.pointIcon}>
            {parse(replaceGlobalConfigVars(globalPartnerCfg.onboarding1.content ?? ""))}

            {partnerCfg && coupon && (
              <CouponBox partnerCfg={partnerCfg} coupon={coupon} />
            )}

            {checkboxOptions.length > 0 && (
              <OptionsContainer>
                {checkboxOptions.map((option) => (
                  <OptionLabel
                    key={option.analyticsKey}
                    control={
                      <StyledCheckbox
                        disableRipple
                        icon={<UncheckedIcon />}
                        checkedIcon={<CheckedIcon />}
                        checked={selectedOption === option.analyticsKey}
                        onChange={handleOptionChange(option)}
                      />
                    }
                    label={
                      <div
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("a")) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                      >
                        {parse(replaceGlobalConfigVars(option.text))}
                      </div>
                    }
                  />
                ))}
              </OptionsContainer>
            )}

            {dropdownHeading && dropdowns.length > 0 && (
              <>
                <Divider sx={{ marginTop: "16px", marginBottom: "16px" }} />
                <HelpHeading>
                  {parse(replaceGlobalConfigVars(dropdownHeading))}
                </HelpHeading>
                {dropdowns.map((dropdown, idx) => (
                  <Dropdown
                    key={idx}
                    items={[
                      parse(replaceGlobalConfigVars(dropdown.heading)),
                      <DropdownContent
                        key={`dropdown-content-${idx}`}
                        content={dropdown.content}
                        iconUrl={globalPartnerCfg.onboarding1.pointIcon}
                      />,
                    ]}
                  />
                ))}
              </>
            )}
          </OnboardingContent>
        </OnboardingStep>
      </Box>

      <StickyCTA
        legalContent={parse(replaceGlobalConfigVars(globalPartnerCfg.onboarding1.legal ?? ""))}
        ctaText={getPlatformFormattedText(globalPartnerCfg.onboarding1.ctaText ?? "Redeem")}
        ctaAction={() => {
          void handleCtaClick();
        }}
        onHeightChange={handleStickyHeightChange}
      />

      <SelectionDialog
        open={showSelectionPrompt}
        onClose={() => setShowSelectionPrompt(false)}
        aria-labelledby="competition-selection-prompt"
      >
        <DialogContent sx={{ padding: 0 }}>
          <DialogHeader>
            <DialogTitle id="competition-selection-prompt">
              {parse(replaceGlobalConfigVars(selectionPrompt.title))}
            </DialogTitle>
            <CloseIconButton
              aria-label="Close selection prompt"
              onClick={() => setShowSelectionPrompt(false)}
            >
              <HiX size={20} />
            </CloseIconButton>
          </DialogHeader>
          <DialogBody>
            {parse(replaceGlobalConfigVars(selectionPrompt.description))}
          </DialogBody>
          <DialogFooter>
            <RewardsButton clickHandler={() => setShowSelectionPrompt(false)}>
              {selectionPrompt.ctaText}
            </RewardsButton>
          </DialogFooter>
        </DialogContent>
      </SelectionDialog>
    </Box>
  );
}

export default AdobeCompetitionOnboarding;
